import React, { useEffect } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { KTCard } from "../../_metronic/helpers";
import { ChartsWidget2 } from "../../_metronic/partials/widgets";
import { ChartsWidget } from "../../_metronic/partials/widgets/charts/ChartsWidget";
import { CountryWiseUsersChart } from "../../_metronic/partials/widgets/charts/CountryWiseUsersChart";
import { EventsPerMonthChart } from "../../_metronic/partials/widgets/charts/EventsPerMonthChart";
import { UsersPerMonthChart } from "../../_metronic/partials/widgets/charts/UsersPerMonthChart";
import { getDashboardInfo } from "../../api";
import "./Dashboard.css";

type DashboardStatCard = {
  id: string;
  title: string;
  value: React.ReactNode;
  caption: string;
  accent: "navy" | "emerald" | "coral" | "amber" | "sky" | "teal";
  icon: React.ReactNode;
  onClick?: () => void;
  valueClassName?: string;
};

const UserGroupIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 14a3 3 0 1 1 0-6 3 3 0 0 1 0 6ZM9 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
      fill="currentColor"
      opacity="0.32"
    />
    <path
      d="M17.6 15c3.41.04 6.19 1.76 6.4 5.4.01.15 0 .6-.54.6H19.6c0-2.25-.74-4.33-2-6ZM.01 20.2C.39 15.43 4.26 13 8.98 13c4.79 0 8.72 2.29 9.01 7.2.01.2 0 .8-.75.8H.73c-.25 0-.75-.54-.72-.8Z"
      fill="currentColor"
    />
  </svg>
);

const LocationPinIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.17 8.94c-.53 1.06-1.24 2.12-1.96 3.07A31.19 31.19 0 0 1 8 14.58a31.19 31.19 0 0 1-2.21-2.57c-.72-.95-1.43-2.01-1.96-3.07C3.3 7.87 3 6.86 3 6a5 5 0 1 1 10 0c0 .86-.3 1.87-.83 2.94Z"
      fill="currentColor"
      opacity="0.26"
    />
    <path
      d="M8 16S2 10.31 2 6a6 6 0 1 1 12 0c0 4.31-6 10-6 10Zm0-8a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
      fill="currentColor"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v2H2V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Z"
      fill="currentColor"
      opacity="0.28"
    />
    <path
      d="M2 11h20v8a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-8Zm5 3a1 1 0 0 0 0 2h3a1 1 0 1 0 0-2H7Zm0 4a1 1 0 0 0 0 2h7a1 1 0 1 0 0-2H7Z"
      fill="currentColor"
    />
  </svg>
);

const PopularityIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="m12 2 1.9 4.84L19 8l-3.88 3.15L16.3 16 12 13.36 7.7 16l1.18-4.85L5 8l5.1-1.16L12 2Z"
      fill="currentColor"
      opacity="0.3"
    />
    <path
      d="M7 20c0-1.66 2.24-3 5-3s5 1.34 5 3v1H7v-1Zm5-2.5a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Z"
      fill="currentColor"
    />
  </svg>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { mutateAsync: getDashboardData, data: dashboardData } =
    useMutation(getDashboardInfo);

  const dashboardResult = dashboardData?.result;
  const mostSearchedLocation =
    dashboardResult?.topSearchLocation || "No recent data";
  const eventCategoryWiseLabel =
    dashboardResult?.eventCategoryWise?.data?.labels || [];
  const eventCategoryWiseData =
    dashboardResult?.eventCategoryWise?.data?.datasets[0]?.data || [];
  const eventLocationWiseLabel =
    dashboardResult?.eventLocationWise?.data?.labels || [];
  const eventLocationWiseData =
    dashboardResult?.eventLocationWise?.data?.datasets[0]?.data || [];
  const popularEventIds = dashboardResult?.popularEventIds ?? "";
  const popularEventCount = dashboardResult?.popularEventCount ?? 0;
  const popularEventsRoute = popularEventIds
    ? `/popular-events/${encodeURIComponent(popularEventIds)}`
    : "/popular-events";

  const countryWiseLabels =
    dashboardResult?.countryWiseUserCount?.data?.labels || [];
  const countryWiseData =
    dashboardResult?.countryWiseUserCount?.data?.datasets[0]?.data || [];

  const overviewCards: DashboardStatCard[] = [
    {
      id: "all-users",
      title: "All Users",
      value: dashboardResult?.totalUserCount ?? 0,
      caption: "Open analytics",
      accent: "navy",
      icon: <UserGroupIcon />,
      onClick: () => navigate("/user-analytics"),
    },
    {
      id: "active-users",
      title: "Active Users",
      value: dashboardResult?.activeUser ?? 0,
      caption: "Currently active accounts",
      accent: "emerald",
      icon: <UserGroupIcon />,
    },
    {
      id: "inactive-users",
      title: "Inactive Users",
      value: dashboardResult?.inactiveUserCount ?? 0,
      caption: "Users needing re-engagement",
      accent: "coral",
      icon: <UserGroupIcon />,
    },
    {
      id: "location-spotlight",
      title: "Location Spotlight",
      value: mostSearchedLocation,
      caption: "Most Visited Location",
      accent: "amber",
      icon: <LocationPinIcon />,
      valueClassName: "dashboard-stat-value--location",
    },
    {
      id: "total-events",
      title: "Total Groups",
      value: dashboardResult?.totalEventCount ?? 0,
      caption: "Open analytics",
      accent: "sky",
      icon: <CalendarIcon />,
      onClick: () => navigate("/all-events"),
    },
    {
      id: "popular-events",
      title: "Popular Groups",
      value: popularEventCount,
      caption:
        "Popularity is based on the last 15 days of activity. Open analytics for a custom date range.",
      accent: "teal",
      icon: <PopularityIcon />,
      onClick: () => navigate(popularEventsRoute),
    },
  ];

  useEffect(() => {
    getDashboardData({});
  }, [getDashboardData]);

  const handleMetricCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    onClick?: () => void,
  ) => {
    if (!onClick) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <KTCard className="dashboard-shell border-0" utilityP={6}>
      <div className="dashboard-header">
        <div>
          <span className="dashboard-eyebrow">Overview</span>
          <h3 className="dashboard-heading">Dashboard</h3>
          <p className="dashboard-subtitle">
            A quick pulse on users, groups, and where activity is flowing right
            now.
          </p>
        </div>
      </div>

      <div className="dashboard-overview-grid">
        {overviewCards.map((card) => (
          <div
            key={card.id}
            className={`dashboard-stat-card dashboard-stat-card--${
              card.accent
            }${card.onClick ? " dashboard-stat-card--interactive" : ""}`}
            onClick={card.onClick}
            onKeyDown={(event) => handleMetricCardKeyDown(event, card.onClick)}
            role={card.onClick ? "button" : undefined}
            tabIndex={card.onClick ? 0 : undefined}
          >
            <div className="dashboard-stat-card__inner">
              <div className="dashboard-stat-card__top">
                <span className="dashboard-stat-icon">{card.icon}</span>
                <span
                  className={`dashboard-stat-chip${
                    card.onClick ? "" : " dashboard-stat-chip--muted"
                  }`}
                >
                  {card.onClick ? "Analytics" : "Live"}
                </span>
              </div>

              <div className="dashboard-stat-copy">
                <span className="dashboard-stat-title">{card.title}</span>
                <span
                  className={`dashboard-stat-value${
                    card.valueClassName ? ` ${card.valueClassName}` : ""
                  }`}
                >
                  {card.value}
                </span>
                <span className="dashboard-stat-caption">{card.caption}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-chart-section">
        <div className="dashboard-chart-section__intro">
          <div>
            <span className="dashboard-chart-section__eyebrow">
              Insights Board
            </span>
            <h4 className="dashboard-chart-section__title">
              Softer, clearer graphs that keep the trend in focus
            </h4>
          </div>
          <p className="dashboard-chart-section__copy">
            A lighter visual treatment makes usage, group growth, and demand
            patterns easier to scan at a glance.
          </p>
        </div>

        <div className="row g-5 g-xl-8 justify-content-center">
          <div className="col-xl-6 col-lg-8 col-md-12 mb-5">
            <CountryWiseUsersChart
              className="card-xl-stretch dashboard-chart-card--feature"
              labels={countryWiseLabels}
              dataList={countryWiseData}
              title="Country-wise Users"
              eyebrow="Audience Mix"
              description="See where your strongest user clusters are building across countries."
            />
          </div>
        </div>

        <div className="row g-5 g-xl-8">
          <div className="col-xl-6 col-lg-6 col-md-12 mb-5">
            <UsersPerMonthChart
              className="card-xl-stretch dashboard-chart-card--mint"
              eyebrow="Growth Pulse"
            />
          </div>
          <div className="col-xl-6 col-lg-6 col-md-12 mb-5">
            <EventsPerMonthChart
              className="card-xl-stretch dashboard-chart-card--coral"
              eyebrow="Publishing Flow"
            />
          </div>
        </div>

        <div className="row g-5 g-xl-8">
          <div className="col-md-6 col-lg-6 col-xl-12 col-xxl-12 mb-md-5 mb-xl-10">
            <ChartsWidget2
              className="card-xl-stretch mb-5 mb-xl-8 dashboard-chart-card--navy"
              label={eventLocationWiseLabel}
              dataList={eventLocationWiseData}
              title="Group Location Wise"
              eyebrow="Location Demand"
              description="Quickly spot the destinations drawing the strongest group attention."
            />
          </div>
          <div className="col-md-6 col-lg-6 col-xl-12 col-xxl-12 mb-md-5 mb-xl-10">
            <ChartsWidget
              className="card-xl-stretch mb-5 mb-xl-8 dashboard-chart-card--amber"
              label={eventCategoryWiseLabel}
              dataList={eventCategoryWiseData}
              title="Group Category Wise"
              eyebrow="Category Spread"
              description="Understand which group types are contributing most to overall activity."
            />
          </div>
        </div>
      </div>
    </KTCard>
  );
};

export default Dashboard;
